import { ThemeName } from "./palette";

export type ThemePreference = "light" | "dark" | "system";
export const KEY = "docubird-theme";
// Context-scoped keys: the Teams tab, the standalone browser embed, and the
// normal pages each keep their own preference so a choice made in one host
// never leaks into another. Mirrored by the pre-paint scripts in the .html files.
export const TEAMS_KEY = `${KEY}-teams`;
export const EMBED_KEY = `${KEY}-embed`;
// Last Teams client theme reported by getContext/onThemeChange ("light"|"dark").
// Cached so the next load's pre-paint script and initial resolve can follow the
// Teams client instead of the OS, which would flash when the two differ.
export const TEAMS_CLIENT_THEME_KEY = "docubird-teams-client-theme";

export const readTeamsClientTheme = (): ThemeName | null => {
  const v = getIO().local.get(TEAMS_CLIENT_THEME_KEY);
  return v === "light" || v === "dark" ? v : null;
};

// A null report invalidates the cache so a stale value can't keep re-seeding
// the wrong theme on later loads.
export const writeTeamsClientTheme = (theme: ThemeName | null): void => {
  const local = getIO().local;
  if (theme === "light" || theme === "dark") local.set(TEAMS_CLIENT_THEME_KEY, theme);
  else local.remove?.(TEAMS_CLIENT_THEME_KEY);
};

export interface ThemeIO {
  local: { get(k: string): string | null; set(k: string, v: string): void; remove?(k: string): void };
  roaming?: { get(k: string): any; set(k: string, v: any): void; save(): void };
}

const valid = (v: any): v is ThemePreference => v === "light" || v === "dark" || v === "system";

export const readPreference = (io: ThemeIO, key: string = KEY, fallback: ThemePreference = "system"): ThemePreference => {
  try { const r = io.roaming?.get(key); if (valid(r)) return r; } catch { /* noop */ }
  try { const l = io.local.get(key); if (valid(l)) return l; } catch { /* noop */ }
  return fallback;
};

export const writePreference = (pref: ThemePreference, io: ThemeIO, key: string = KEY): void => {
  try { io.local.set(key, pref); } catch { /* private mode */ }
  try { io.roaming?.set(key, pref); io.roaming?.save(); } catch { /* not Outlook / offline */ }
};

export const getIO = (): ThemeIO => {
  const O = (window as any).Office;
  const isOutlook = !!O?.context?.mailbox && !!O?.context?.roamingSettings;
  const roaming = isOutlook
    ? {
        get: (k: string) => O.context.roamingSettings.get(k),
        set: (k: string, v: any) => O.context.roamingSettings.set(k, v),
        save: () => O.context.roamingSettings.saveAsync(),
      }
    : undefined;
  return {
    local: {
      get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
      set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* noop */ } },
      remove: (k) => { try { localStorage.removeItem(k); } catch { /* noop */ } },
    },
    roaming,
  };
};
